import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as supplierService from "../../services/supplierService";
import * as hrmsService from "../../services/hrmsService";
import { ChatPanel } from "../Proc/Chat";
import { DataTable, ProcBadge, act, amount, fileSize, linkBtn, openSigned, orderKey } from "../Proc/procUi";
import { DetailGrid, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, cardStyle, formatDate, formatDateTime, inputStyle, pageStyle, todayYmd, useLoad } from "../../components/ui";

// A supplier's invoice for the order: number, date, amount and the file. The buyer's bill in Payables is drafted from it
// once the goods have been received.
function InvoiceDrawer({ order, onHide, onDone }) {
  const [form, setForm] = useState({ invoice_number: "", invoice_date: todayYmd(), amount: String(order.total) });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const send = async () => {
    setBusy(true);
    try {
      const res = await supplierService.sendInvoice(order.id, form, file);
      if (res?.success) {
        toast.success(res.message || "Invoice sent");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } catch (err) {
      toast.error(err.message || "The invoice could not be sent");
    } finally {
      setBusy(false);
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title="Send an invoice" width={460} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={send} disabled={busy || !file}>Submit invoice</PrimaryButton></>}>
      <Field label="Invoice number" required htmlFor="inv-number">
        <input id="inv-number" className="form-control" style={inputStyle} maxLength={60} value={form.invoice_number} onChange={set("invoice_number")} />
      </Field>
      <Field label="Invoice date" required htmlFor="inv-date">
        <input id="inv-date" type="date" className="form-control" style={inputStyle} value={form.invoice_date} onChange={set("invoice_date")} />
      </Field>
      <Field label={`Invoice amount (${order.currency})`} required htmlFor="inv-amount" hint="The total on the invoice, tax included">
        <input id="inv-amount" type="number" min={0} className="form-control" style={inputStyle} value={form.amount} onChange={set("amount")} />
      </Field>
      <Field label="Invoice file" required htmlFor="inv-file" hint="PDF, JPG, PNG or DOCX, up to 10 MB">
        <input id="inv-file" type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)} />
      </Field>
    </SlidePanel>
  );
}

// One order for the supplier: what the buyer is ordering, accept / reject while it waits for an answer, then ship it
// and send the invoice.
export default function SupplierOrderDetail() {
  const { id } = useParams();
  const { data: o, loading, error, reload } = useLoad(() => supplierService.getOrder(id), [id]);
  const [rejecting, setRejecting] = useState(false);
  const [chatting, setChatting] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [moving, setMoving] = useState(false);

  const ship = (status) => act(() => supplierService.shipOrder(id, status), "Saved").then((r) => r && reload());
  const accept = () => act(() => supplierService.acceptOrder(id), "Order accepted").then((r) => r && reload());
  const waiting = o && o.status === 3;
  const live = o && [6, 8, 9].includes(o.status); // accepted and not yet received
  const canInvoice = o && [6, 8, 9, 10].includes(o.status);

  return (
    <div style={pageStyle}>
      <PageHeader
        title={o ? `Order ${o.order_number}` : "Order"}
        subtitle={o ? `${o.buyer_name} · ${o.rfq_number}: ${o.rfq_title}` : ""}
        actions={
          o ? (
            <span className="d-flex flex-wrap gap-2 align-items-center">
              <ProcBadge status={orderKey(o.status)} label={waiting ? "Waiting for your answer" : o.status_label} />
              {waiting ? <PrimaryButton onClick={accept}>Accept order</PrimaryButton> : null}
              {waiting ? <SecondaryButton onClick={() => setRejecting(true)}>Reject order</SecondaryButton> : null}
              {o.status === 6 ? <PrimaryButton onClick={() => ship("SHIPPED")}>Mark shipped</PrimaryButton> : null}
              {o.status === 6 || o.status === 8 ? <SecondaryButton onClick={() => ship("OUT_FOR_DELIVERY")}>Out for delivery</SecondaryButton> : null}
              {canInvoice ? <SecondaryButton onClick={() => setInvoicing(true)}>Send invoice</SecondaryButton> : null}
              <SecondaryButton onClick={() => setChatting(true)}>Message the buyer</SecondaryButton>
            </span>
          ) : null
        }
      />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {o ? (
          <>
            <div style={{ ...cardStyle, padding: 16 }} className="mb-4">
              <DetailGrid
                columns={3}
                items={[
                  ["Buyer", o.buyer_name],
                  ["Total", `${o.currency} ${amount(o.total)}`],
                  ["Delivery date", o.delivery_date ? formatDate(o.delivery_date) : null],
                  ["Delivery location", o.delivery_location],
                  ["Payment terms", o.payment_terms],
                  ["Instructions", o.instructions],
                ]}
              />
              {live ? <div className="mt-3" style={{ fontSize: 13 }}><button type="button" style={linkBtn} onClick={() => setMoving(true)}>Delivery date changed?</button></div> : null}
              {o.status === 4 && o.reject_reason ? <div className="mt-3" style={{ fontSize: 13, color: "#991B1B" }}>Rejected: {o.reject_reason}</div> : null}
              {waiting ? <div className="mt-3" style={{ fontSize: 13, color: "#374151" }}>Accepting confirms you will supply these items at the prices below. If you cannot, reject the order and tell the buyer why.</div> : null}
            </div>
            <h6>Items</h6>
            <DataTable
              minWidth={520}
              rowKey={(l) => l.id}
              rows={o.lines}
              columns={[
                { label: "Item", render: (l) => `${l.line_no}. ${l.item}` },
                { label: "Quantity", render: (l) => `${l.quantity} ${l.uom || ""}` },
                { label: "Unit price", render: (l) => amount(l.unit_price) },
                { label: "Amount", render: (l) => amount(l.amount) },
              ]}
              footer={<tr><th colSpan={3} style={{ padding: "10px 12px" }}>Total ({o.currency})</th><th style={{ padding: "10px 12px" }}>{amount(o.total)}</th></tr>}
            />
            {o.invoices.length ? (
              <>
                <h6 className="mt-4">Your invoices</h6>
                <DataTable
                  minWidth={480}
                  rowKey={(i) => i.invoice_number}
                  rows={o.invoices}
                  columns={[
                    { label: "Invoice", render: (i) => <button type="button" style={linkBtn} onClick={() => openSigned(() => supplierService.getInvoiceUrl(i.id), toast)}>{i.invoice_number}</button> },
                    { label: "Date", render: (i) => formatDate(i.invoice_date) },
                    { label: "Amount", render: (i) => `${i.currency} ${amount(i.amount)}` },
                    { label: "File", render: (i) => `${i.file_name} · ${fileSize(i.size)}` },
                  ]}
                />
              </>
            ) : null}
            <h6 className="mt-4">History</h6>
            <DataTable
              minWidth={420}
              rowKey={(h) => h.id}
              rows={o.history}
              columns={[
                { label: "When", render: (h) => formatDateTime(h.created_at), style: { whiteSpace: "nowrap" } },
                { label: "What", render: (h) => h.note || h.status },
                { label: "By", render: (h) => ({ SYSTEM: "System", BUYER: o.buyer_name, SUPPLIER: "You" }[h.actor_role]) },
              ]}
            />
            {chatting ? <ChatPanel service={supplierService} refQuery={{ buyer_id: o.buyer_id }} title={o.buyer_name} subtitle={o.order_number} onClose={() => setChatting(false)} /> : null}
          </>
        ) : null}
      </LoadState>
      {invoicing && o ? <InvoiceDrawer order={o} onHide={() => setInvoicing(false)} onDone={reload} /> : null}
      <FormDrawer
        show={moving}
        title="New delivery date"
        submitLabel="Save date"
        initial={{ delivery_date: o && o.delivery_date ? String(o.delivery_date).slice(0, 10) : "" }}
        onHide={() => setMoving(false)}
        onSave={(clean) => supplierService.setOrderDeliveryDate(id, clean.delivery_date)}
        onDone={reload}
        fields={[{ key: "delivery_date", label: "When will it arrive?", type: "date", required: true, hint: "The buyer is told" }]}
      />
      <FormDrawer
        show={rejecting}
        title="Reject this order"
        submitLabel="Confirm rejection"
        initial={{}}
        onHide={() => setRejecting(false)}
        onSave={(clean) => supplierService.rejectOrder(id, clean.reason)}
        onDone={reload}
        fields={[{ key: "reason", label: "Why can you not supply this order?", type: "textarea", required: true, max: 500, hint: "The buyer sees this and the purchase order is cancelled" }]}
      />
    </div>
  );
}
